export type LoomNativeHybridDType = "float32" | "uint32";

export interface LoomNativeHybridArrayDescriptor {
  dtype: LoomNativeHybridDType;
  byteOffset: number;
  count: number;
  shape: number[];
}

function integer(value: unknown, label: string): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0) {
    throw new Error(`${label} must be a non-negative safe integer`);
  }
  return value;
}

export function validateLoomNativeHybridArrayDescriptors(
  arrays: Record<string, unknown>, payloadByteLength: number,
): asserts arrays is Record<string, LoomNativeHybridArrayDescriptor> {
  integer(payloadByteLength, "Loom native-hybrid payload byte length");
  const ranges: Array<{ end: number; name: string; start: number }> = [];
  for (const [name, value] of Object.entries(arrays)) {
    if (!name || typeof value !== "object" || value === null || Array.isArray(value)) {
      throw new Error(`Array descriptor ${name || "<empty>"} is invalid`);
    }
    const raw = value as Record<string, unknown>;
    const keys = Object.keys(raw);
    if (keys.length !== 4
        || keys.some((key) => !["dtype", "byteOffset", "count", "shape"].includes(key))
        || (raw.dtype !== "float32" && raw.dtype !== "uint32")
        || !Array.isArray(raw.shape)) {
      throw new Error(`Array descriptor ${name} has an unsupported field set`);
    }
    const offset = integer(raw.byteOffset, `Array descriptor ${name}.byteOffset`);
    const count = integer(raw.count, `Array descriptor ${name}.count`);
    const shape = raw.shape.map((item, index) => (
      integer(item, `Array descriptor ${name}.shape[${index}]`)
    ));
    const shapeCount = shape.reduce((product, item) => product * item, 1);
    const end = offset + count * 4;
    if (offset % 4 !== 0 || shapeCount !== count || !Number.isSafeInteger(end)
        || end > payloadByteLength) {
      throw new Error(`Array descriptor ${name} lies outside the payload or has an invalid extent`);
    }
    if (count > 0) ranges.push({ start: offset, end, name });
  }
  ranges.sort((left, right) => left.start - right.start || left.end - right.end);
  for (let index = 1; index < ranges.length; index += 1) {
    const previous = ranges[index - 1]!; const current = ranges[index]!;
    if (current.start < previous.end) {
      throw new Error(`Array descriptors ${previous.name} and ${current.name} overlap`);
    }
  }
}
